import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Heart, MapPin, Phone, Mail, Clock, Utensils, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO, ORG } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

function LangToggle({ className = "" }) {
  const { lang, setLang, t } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-[#E2E8F0] overflow-hidden ${className}`} role="group" aria-label={t.langLabel} data-testid="lang-toggle">
      <button
        type="button"
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        data-testid="lang-en"
        className={`px-3 py-1.5 text-sm font-bold transition-colors ${lang === "en" ? "bg-[#0A2540] text-white" : "text-[#0A2540] hover:bg-[#F1F5F9]"}`}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLang("es")}
        aria-pressed={lang === "es"}
        data-testid="lang-es"
        className={`px-3 py-1.5 text-sm font-bold transition-colors ${lang === "es" ? "bg-[#0A2540] text-white" : "text-[#0A2540] hover:bg-[#F1F5F9]"}`}
      >
        ES
      </button>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  const NAV = [
    { to: "/", label: t.nav.home, end: true, id: "home" },
    { to: "/about", label: t.nav.about, id: "about" },
    { to: "/programs", label: t.nav.programs, id: "programs" },
    { to: "/get-involved", label: t.nav.getInvolved, id: "get-involved" },
  ];

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm font-semibold transition-colors px-1 py-1 ${isActive ? "text-[#16A34A]" : "text-[#0A2540] hover:text-[#16A34A]"}`;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all ${scrolled ? "backdrop-blur-xl bg-white/85 shadow-sm border-b border-[#E2E8F0]" : "bg-white/60 backdrop-blur-sm"}`}
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-white focus:px-4 focus:py-2 focus:z-50">
        Skip to main content
      </a>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img src={LOGO} alt={`${ORG.name} logo`} className="h-14 w-14 object-contain" />
            <span className="sr-only">{ORG.name}</span>
          </Link>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} data-testid={`nav-${n.id}-link`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangToggle className="hidden sm:flex" />
            <Button asChild className="hidden sm:inline-flex bg-[#E85D04] hover:bg-[#CC5202] text-white font-semibold rounded-full px-6 h-11 shadow-sm hover:shadow-md transition-all">
              <Link to="/donate" data-testid="header-donate-button">
                <Heart className="w-4 h-4 mr-2" aria-hidden="true" /> {t.donateNow}
              </Link>
            </Button>
            <button
              className="lg:hidden text-[#0A2540] p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white" data-testid="mobile-menu">
          <nav aria-label="Mobile" className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) => `py-3 px-2 rounded-lg font-semibold ${isActive ? "text-[#16A34A] bg-[#F1F5F9]" : "text-[#0A2540]"}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-2"><LangToggle /></div>
            <Button asChild className="mt-2 bg-[#E85D04] hover:bg-[#CC5202] text-white font-semibold rounded-full h-12">
              <Link to="/donate" data-testid="mobile-donate-button">
                <Heart className="w-4 h-4 mr-2" /> {t.donateNow}
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { t } = useLang();
  const NAV = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/programs", label: t.nav.programs },
    { to: "/get-involved", label: t.nav.getInvolved },
  ];
  return (
    <footer className="bg-[#0A2540] text-white mt-auto" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO} alt="" aria-hidden="true" className="h-16 w-16 object-contain bg-white rounded-xl p-1.5" />
            <span className="font-display font-bold text-lg">Cornerstone FOCCUS</span>
          </div>
          <p className="text-sm text-white/70 leading-relaxed max-w-xs">{t.footer.tagline}</p>
          <a
            href={ORG.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Cornerstone FOCCUS Food Pantry on Facebook"
            data-testid="facebook-link"
            className="mt-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-[#16A34A] transition-colors"
          >
            <Facebook className="w-5 h-5 text-white" aria-hidden="true" />
          </a>
        </div>

        <div className="md:col-span-3">
          <h3 className="font-display font-semibold text-white mb-4 text-base">{t.footer.explore}</h3>
          <ul className="space-y-3 text-sm text-white/70">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-[#16A34A] transition-colors">{n.label}</Link>
              </li>
            ))}
            <li><Link to="/donate" className="hover:text-[#16A34A] transition-colors">{t.nav.donate}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-5">
          <h3 className="font-display font-semibold text-white mb-4 text-base">{t.footer.visitContact}</h3>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex gap-3"><MapPin className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" /> {ORG.address}</li>
            <li className="flex gap-3"><Phone className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" /> <a href={`tel:${ORG.phone}`} className="hover:text-[#16A34A]">{ORG.phone}</a></li>
            <li className="flex gap-3"><Mail className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" /> <a href={`mailto:${ORG.email}`} className="hover:text-[#16A34A]">{ORG.email}</a></li>
            <li className="flex gap-3"><Utensils className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" /> <span>{t.footer.foodAssistance} <a href={`mailto:${ORG.helpEmail}`} className="hover:text-[#16A34A]">{ORG.helpEmail}</a></span></li>
            <li className="flex gap-3"><Clock className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" />
              <span>{ORG.hours.map((h) => `${h.day} ${h.time}`).join(" · ")}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} {ORG.name}. {t.footer.rights}</p>
          <p>{t.footer.disclosure(ORG.ein)}</p>
          <Link to="/admin/login" className="hover:text-white/80 transition-colors" data-testid="staff-login-link">Staff Login</Link>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="flex flex-col min-h-screen bg-[#FAFAFA]">
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
